import './TQServiceCard.scss';

import React from 'react';

import moment from 'moment';
import { PiUsers } from 'react-icons/pi';

import CustomAvatar from '../../../../components/CustomAvatar/CustomAvatar';
import CustomTag from '../../../../components/CustomTag/CustomTag';
import { DELETED_USER } from '../../../../shared/data';
import { checkIfCurrentLoggedInUser } from '../../../../shared/utils';
import {
  ITQServiceRequest,
  TQServiceRequestStatus,
  TQServiceRequestTypeValue,
  TQServiceRequestVisibilityType,
} from '../../../../types/tqConnectTypes/service-requests';
import { SERVICE_REQUEST_STATUS_COLORS } from '../../constants';
import ServiceRequestStatusTag
  from '../TQServiceRequestResponseBottpmSheet/ServiceRequestStatusTag';
import TQServiceRequestCardInfo from './TQServiceRequestCardInfo';

export interface TQServiceCardCreator {
  avatarUrl?: string | null;
  name: string;
  actionLabel: string;
  timestamp: string;
}

export interface TQServiceCardProps {
  /** Request visibility: public (blue pill) or private (purple pill) */
  type: TQServiceRequestVisibilityType;
  title: string;
  description: string;
  tags: string[];
  creator: TQServiceCardCreator;
  /** Shown when creator is current user (e.g. "6 Help Offered") */
  helpOfferedCount?: number;
  /** Status pill on bottom-right; omit to hide */
  status?: TQServiceRequestStatus;
  /** Show "Open" button for open requests (e.g. when requested from someone) */
  showOpenButton?: boolean;
  onClick?: () => void;
  onOpenClick?: () => void;
  className?: string;
  requestType: TQServiceRequestTypeValue;
  serviceRequest: ITQServiceRequest;
}

const TQServiceCard: React.FC<TQServiceCardProps> = ({
  title,
  description,
  tags,
  creator,
  helpOfferedCount,
  status,
  showOpenButton = false,
  onClick,
  onOpenClick,
  className = '',
  requestType,
  serviceRequest,
}) => {
  const showHelpOffered =
    helpOfferedCount !== undefined && helpOfferedCount >= 0;

  const { visibility, requestedBy, requestedTo, createdAt } = serviceRequest;

  const isMyRequest = checkIfCurrentLoggedInUser(requestedBy?._id);
  const isRequestedToMe = checkIfCurrentLoggedInUser(requestedTo?._id || '');
  const isRequestedToMePrivately =
    visibility === 'private' && isRequestedToMe && status === 'open';

  return (
    <>
      <TQServiceRequestCardInfo
        requestType={requestType}
        serviceRequest={serviceRequest}
        isMyRequest={isMyRequest}
        isRequestedToMe={isRequestedToMe}
        isRequestedToMePrivately={isRequestedToMePrivately}
      />
      <article
        className={`tq-service-card ${onClick ? 'tq-service-card--clickable' : ''} ${className}`.trim()}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onClick={onClick}
        onKeyDown={
          onClick
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick();
                }
              }
            : undefined
        }>
        {/* Request type header */}
        {visibility && requestType !== 'discover' ? (
          <ServiceRequestStatusTag visibility={visibility} />
        ) : null}

        {/* Title & description */}
        <h3 className="tq-service-card__title">{title}</h3>
        <p
          className="tq-service-card__description"
          dangerouslySetInnerHTML={{ __html: description ?? '' }}
        />

        {/* Tags */}
        {tags.length > 0 && (
          <div className="tq-service-card__tags" role="list">
            {tags.map((tag) => (
              <span key={tag} className="tq-service-card__tag" role="listitem">
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Footer: creator info (left) + engagement/status (right) */}
        <div className="tq-service-card__footer">
          <div className="tq-service-card__creator">
            <CustomAvatar
              src={creator.avatarUrl ?? undefined}
              name={!creator.avatarUrl ? creator.name : undefined}
              size={28}
              border={0}
              className="tq-service-card__creator-avatar"
            />
            <div className="tq-service-card__creator-meta">
              <span className="tq-service-card__creator-name">
                {creator.name || DELETED_USER}
              </span>{' '}
              <span className="tq-service-card__creator-context">
                {creator.actionLabel} • {moment(createdAt).fromNow()}
              </span>
            </div>
          </div>

          <div className="tq-service-card__actions">
            {showHelpOffered && (
              <span className="tq-service-card__help-offered">
                {requestType === 'request-received' ? null : (
                  <>
                    <PiUsers size={16} aria-hidden />
                    {helpOfferedCount} Help Offered
                  </>
                )}
              </span>
            )}
            {status !== undefined && (
              <CustomTag
                className="tq-service-card__status"
                color={SERVICE_REQUEST_STATUS_COLORS[status]}
                style={{
                  margin: 0,
                  textTransform: 'none',
                  fontSize: 12,
                  fontWeight: 500,
                }}>
                {status.toTitleCase()}
              </CustomTag>
            )}
            {showOpenButton && status === 'open' && (
              <button
                type="button"
                className="tq-service-card__open-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenClick?.();
                }}>
                Open
              </button>
            )}
          </div>
        </div>
      </article>
    </>
  );
};

export default TQServiceCard;
